import React, { useState } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Wallet, 
  Settings, 
  Users, 
  Target,
  Activity,
  PieChart,
  Shield,
  Zap,
  Eye,
  Bot,
  Search,
  ArrowLeft,
  ExternalLink
} from 'lucide-react';

interface ModulesProps {
  onGoBack: () => void;
  onGoHome: () => void;
  onNavigateToModule: (route: string) => void;
  walletAddress?: string;
  walletProvider?: 'phantom' | 'solflare';
  onShowConnectModal?: () => void;
  onDisconnectWallet?: () => void;
}

// Module definitions
const modules = [
  {
    id: 'portfolio',
    name: 'Portfolio',
    description: 'View and manage your token holdings and performance',
    icon: <PieChart className="w-6 h-6" />,
    color: 'bg-blue-500',
    available: true,
    route: 'portfolio-page'
  },
  {
    id: 'wallets',
    name: 'Wallets',
    description: 'Manage multiple wallets and their connections',
    icon: <Wallet className="w-6 h-6" />,
    color: 'bg-green-500',
    available: true,
    route: 'wallets-page'
  },
  {
    id: 'settings',
    name: 'Settings',
    description: 'Configure your trading preferences and platform settings',
    icon: <Settings className="w-6 h-6" />,
    color: 'bg-gray-500',
    available: true,
    route: 'settings'
  },
  {
    id: 'advanced-charts',
    name: 'Advanced Charts',
    description: 'Professional trading charts with technical analysis',
    icon: <BarChart3 className="w-6 h-6" />,
    color: 'bg-purple-500',
    available: true,
    route: 'charts-page'
  },
  {
    id: 'whale-tracker',
    name: 'Whale Tracker',
    description: 'Monitor large wallet movements and transactions',
    icon: <Eye className="w-6 h-6" />,
    color: 'bg-orange-500',
    available: false,
    route: 'whale-tracker'
  },
  {
    id: 'auto-trader',
    name: 'Auto Trader',
    description: 'Automated trading strategies and execution',
    icon: <Bot className="w-6 h-6" />,
    color: 'bg-red-500',
    available: false,
    route: 'auto-trader'
  },
  {
    id: 'rug-tracker',
    name: 'Rug Tracker',
    description: 'Detect and avoid potential rug pulls and scams',
    icon: <Shield className="w-6 h-6" />,
    color: 'bg-yellow-500',
    available: false,
    route: 'rug-tracker'
  },
  {
    id: 'token-sniper',
    name: 'Token Sniper',
    description: 'Fast execution for new token launches',
    icon: <Target className="w-6 h-6" />,
    color: 'bg-pink-500',
    available: false,
    route: 'token-sniper'
  },
  {
    id: 'performance',
    name: 'Performance Analytics',
    description: 'Detailed performance metrics and trading history',
    icon: <TrendingUp className="w-6 h-6" />,
    color: 'bg-indigo-500',
    available: false,
    route: 'performance'
  },
  {
    id: 'community',
    name: 'Community',
    description: 'Connect with other traders and share strategies',
    icon: <Users className="w-6 h-6" />,
    color: 'bg-cyan-500',
    available: true,
    route: 'community'
  },
  {
    id: 'token-scanner',
    name: 'Token Scanner',
    description: 'Scan and analyze new tokens and opportunities',
    icon: <Search className="w-6 h-6" />,
    color: 'bg-emerald-500',
    available: false,
    route: 'token-scanner'
  },
  {
    id: 'activity-monitor',
    name: 'Activity Monitor',
    description: 'Real-time monitoring of market activity and alerts',
    icon: <Activity className="w-6 h-6" />,
    color: 'bg-violet-500',
    available: false,
    route: 'activity-monitor'
  }
];

const Modules: React.FC<ModulesProps> = ({ 
  onGoBack,
  onGoHome,
  onNavigateToModule,
  walletAddress,
  walletProvider,
  onShowConnectModal,
  onDisconnectWallet
}) => {
  const [hoveredModule, setHoveredModule] = useState<string | null>(null);

  const handleModuleClick = (module: typeof modules[0]) => {
    if (!module.available) {
      return;
    }
    
    // Navigate to the specific module page
    onNavigateToModule(module.route);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 py-8 pt-24 relative overflow-hidden">
      {/* Architectural Background Elements */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Main background image with reduced opacity */}
        <img 
          src="https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/213.png"
          alt="Background"
          className="w-full h-full object-cover opacity-5"
        />
        
        {/* Architectural Lines Grid */}
        <div className="absolute inset-0">
          {/* Horizontal lines */}
          <div className="absolute top-1/4 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-300/40 to-transparent"></div>
          <div className="absolute top-3/4 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-300/40 to-transparent"></div>
          
          {/* Vertical lines */}
          <div className="absolute top-0 left-1/6 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-300/40 to-transparent"></div>
          <div className="absolute top-0 right-1/6 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-300/40 to-transparent"></div>
        </div>
        
        {/* Subtle green glow orbs */}
        <div className="absolute top-32 left-16 w-96 h-96 rounded-full bg-[#24c35b]/8 blur-[100px] animate-pulse"></div>
        <div className="absolute bottom-32 right-16 w-80 h-80 rounded-full bg-gray-300/15 blur-[80px] animate-pulse delay-1000"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-8">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-12">
          <button
            onClick={onGoBack}
            className="flex items-center gap-3 glassmorphism px-6 py-3 rounded-xl border border-gray-200/80 hover:bg-gray-50 transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 text-[#24c35b] group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium text-gray-900">Back to Selection</span>
          </button>
          
          {/* Connection Status */}
          {walletAddress && (
            <div className="glassmorphism p-3 rounded-lg border border-gray-200/80">
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-[#24c35b] mr-2 animate-pulse"></div>
                <span className="text-gray-700 text-sm font-medium">
                  {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Title Section */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 glassmorphism rounded-2xl flex items-center justify-center mr-4">
              <Zap className="w-8 h-8 text-[#24c35b]" />
            </div>
            <div>
              <h1 className="text-5xl lg:text-6xl font-black text-gray-900 leading-tight">
                Trading
                <span className="block text-[#24c35b]">Modules</span>
              </h1>
            </div>
          </div>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Access individual trading tools and focused functionality
          </p>
        </div>

        {/* Modules Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {modules.map((module) => (
            <div
              key={module.id}
              className={`glassmorphism p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-2xl ${
                module.available
                  ? hoveredModule === module.id
                    ? 'border-[#24c35b]/60 shadow-[0_0_30px_rgba(36,195,91,0.3)]'
                    : 'border-gray-200/80 hover:border-[#24c35b]/40'
                  : 'border-gray-200/50 opacity-60 cursor-not-allowed'
              }`}
              onMouseEnter={() => module.available && setHoveredModule(module.id)}
              onMouseLeave={() => setHoveredModule(null)}
              onClick={() => handleModuleClick(module)}
            >
              <div className="text-center">
                {/* Icon Container */}
                <div className={`w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center transition-all duration-300 ${
                  module.available
                    ? hoveredModule === module.id
                      ? 'bg-[#24c35b]/20 shadow-lg'
                      : 'bg-gray-100'
                    : 'bg-gray-50'
                }`}>
                  <div className={`transition-colors duration-300 ${
                    module.available
                      ? hoveredModule === module.id 
                        ? 'text-[#24c35b]' 
                        : 'text-gray-600'
                      : 'text-gray-400'
                  }`}>
                    {module.icon}
                  </div>
                </div>
                
                {/* Title */}
                <h3 className={`text-lg font-bold mb-2 ${
                  module.available ? 'text-gray-900' : 'text-gray-500'
                }`}>
                  {module.name}
                </h3>
                
                {/* Description */}
                <p className={`text-sm leading-relaxed mb-4 ${
                  module.available ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {module.description}
                </p>
                
                {/* Status */}
                <div className="flex items-center justify-center">
                  {module.available ? (
                    <div className="flex items-center gap-1 text-xs font-medium text-[#24c35b]">
                      <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
                      Available
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-xs font-medium text-gray-400">
                      <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                      Coming Soon
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="mt-16 text-center">
          <div className="glassmorphism p-8 rounded-2xl border border-gray-200/80 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Module Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <div className="text-3xl font-black text-[#24c35b] mb-2">
                  {modules.filter(m => m.available).length}
                </div>
                <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  Available Now
                </div>
              </div>
              <div>
                <div className="text-3xl font-black text-gray-500 mb-2">
                  {modules.filter(m => !m.available).length}
                </div>
                <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  In Development
                </div>
              </div>
              <div>
                <div className="text-3xl font-black text-gray-900 mb-2">
                  {modules.length}
                </div>
                <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  Total Modules
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modules; 